import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { useConfirmReceipt } from '../hooks/useBankTransfers';
import type { BankTransferQueueItem, TransferStage } from '../types/bankTransfer';
import { isAxiosError } from 'axios';

interface ConfirmReceiptDialogProps {
  readonly item: BankTransferQueueItem;
  readonly onClose: () => void;
  readonly onSuccess: (message: string) => void;
}

export const ConfirmReceiptDialog = ({ item, onClose, onSuccess }: ConfirmReceiptDialogProps) => {
  const { t } = useTranslation('forms');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [stage, setStage] = useState<TransferStage>(item.expectedStage);
  const [confirmedAmount, setConfirmedAmount] = useState(item.expectedAmount);
  const [note, setNote] = useState('');
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  const mutation = useConfirmReceipt();

  const amountMismatch = confirmedAmount !== item.expectedAmount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNetworkError(false);

    mutation.mutate(
      {
        bookingId: item.bookingId,
        request: {
          stage,
          expectedAmount: item.expectedAmount,
          confirmedAmount,
          currency: item.currency,
          paymentReference: item.paymentReference ?? '',
          note: note.trim() || undefined,
          expectedProcessVersion: item.processVersion,
        },
        idempotencyKey,
      },
      {
        onSuccess: (data) => {
          if (data.resultingProcessStatus != null) {
            onSuccess(t('confirmReceipt.successToast'));
          } else {
            setAlreadyConfirmed(true);
          }
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 409) {
            onClose();
            onSuccess(t('confirmReceipt.staleVersionToast'));
          } else {
            setNetworkError(true);
          }
        },
      },
    );
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Dialog header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {t('confirmReceipt.dialogTitle')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {alreadyConfirmed ? (
          <div className="px-6 py-5">
            <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              {t('confirmReceipt.alreadyConfirmed')}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={onClose}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                {t('confirmReceipt.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Stage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('confirmReceipt.stage')}
              </label>
              <select
                value={stage}
                onChange={e => setStage(e.target.value as TransferStage)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="DEPOSIT">{t('confirmReceipt.stageDeposit')}</option>
                <option value="BALANCE">{t('confirmReceipt.stageBalance')}</option>
                <option value="FULL">{t('confirmReceipt.stageFull')}</option>
              </select>
            </div>

            {/* Confirmed amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('confirmReceipt.confirmedAmount')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={confirmedAmount}
                onChange={e => setConfirmedAmount(parseFloat(e.target.value))}
                placeholder={t('confirmReceipt.confirmedAmountPlaceholder')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                required
              />
              {amountMismatch && (
                <div className="mt-1 flex items-start gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                  {t('confirmReceipt.amountMismatchWarning')}
                </div>
              )}
            </div>

            {/* Reference section (read-only) */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 space-y-1 text-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                {t('confirmReceipt.referenceSection')}
              </p>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('confirmReceipt.expectedAmount')}</span>
                <span className="text-gray-900">
                  {item.expectedAmount.toFixed(2)} {item.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('confirmReceipt.paymentReference')}</span>
                <span className="font-mono text-xs text-gray-700">{item.paymentReference ?? '—'}</span>
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('confirmReceipt.note')}
              </label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={t('confirmReceipt.notePlaceholder')}
                maxLength={1000}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="text-xs text-gray-400 text-right mt-0.5">{note.length}/1000</p>
            </div>

            {networkError && (
              <p className="text-xs text-red-600">{t('confirmReceipt.networkError')}</p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 text-sm font-medium px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {mutation.isPending
                  ? t('confirmReceipt.submitting')
                  : networkError
                  ? t('confirmReceipt.retry')
                  : t('confirmReceipt.submit')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('confirmReceipt.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
